import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useToastNotification } from '@/contexts/ToastContext';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader2, Link as LinkIcon, RefreshCw } from 'lucide-react';
import GmailHeader from './GmailHeader';
import GmailSidebar from './GmailSidebar';
import GmailEmailList from './GmailEmailList';
import GmailMail from './GmailMail';
import GmailSendMail from './GmailSendMail';
import './gmail.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GmailInbox = () => {
  const toast = useToastNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState({
    configured: false,
    connected: false,
    email: null,
    loading: true
  });
  
  // Email state
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedMail, setSelectedMail] = useState(null);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('inbox');
  const [connecting, setConnecting] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  
  // Ref to track if initial load is done
  const initialLoadDone = useRef(false);

  // Check for OAuth callback params
  useEffect(() => {
    const gmailConnected = searchParams.get('gmail_connected');
    const gmailError = searchParams.get('gmail_error');
    const email = searchParams.get('email');
    
    if (gmailConnected === 'true') {
      toast.success(`Gmail connected: ${email}`);
      // Clear params
      setSearchParams({});
      // Refresh status
      checkConnectionStatus();
    } else if (gmailError) {
      toast.error(`Gmail connection failed: ${gmailError}`);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Check connection status on mount
  const checkConnectionStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gmail/oauth/status`, {
        withCredentials: true
      });
      setConnectionStatus({
        ...response.data,
        loading: false
      });
    } catch (error) {
      // Silently ignore 401 errors - session issues handled by auth context
      if (error.response?.status !== 401) {
        console.error('Error checking Gmail status:', error);
      }
      setConnectionStatus({
        configured: false,
        connected: false,
        email: null,
        loading: false
      });
    }
  }, []);

  useEffect(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  // Fetch emails (from cache first, then sync)
  const fetchEmails = useCallback(async (forceRefresh = false) => {
    if (!connectionStatus.connected) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/gmail/messages`, {
        withCredentials: true,
        params: { 
          folder: activeSection, 
          search: searchQuery,
          force_refresh: forceRefresh
        }
      });
      
      const data = response.data;
      setEmails(data.messages || []);
      setFromCache(data.fromCache || false);
      setLastSync(data.lastSync);
      
      // If we got cached data, trigger background sync for updates
      if (data.fromCache && !searchQuery) {
        syncNewEmails();
      }
      
    } catch (error) {
      // Silently ignore 401 errors - session issues handled by auth context
      if (error.response?.status !== 401) {
        console.error('Error fetching emails:', error);
      }
      if (error.response?.status === 503 || error.response?.data?.detail?.includes('invalid_client')) {
        // Gmail not connected or token invalid - needs reconnection
        setConnectionStatus(prev => ({ ...prev, connected: false }));
        toast.error('Gmail connection expired. Please reconnect.');
      }
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }, [activeSection, searchQuery, connectionStatus.connected, toast]);

  // Sync new emails (incremental update)
  const syncNewEmails = useCallback(async () => {
    if (!connectionStatus.connected || syncing) return;
    
    try {
      setSyncing(true);
      const response = await axios.post(`${API_URL}/api/gmail/sync`, null, {
        withCredentials: true,
        params: { folder: activeSection }
      });
      
      const syncResult = response.data;
      
      // If there are new emails, update the list
      if (syncResult.new_emails > 0 || syncResult.modified > 0 || syncResult.deleted > 0) {
        // Fetch fresh list since we have updates
        const emailsResponse = await axios.get(`${API_URL}/api/gmail/messages`, {
          withCredentials: true,
          params: { folder: activeSection, force_refresh: true }
        });
        setEmails(emailsResponse.data.messages || []);
        setFromCache(false);
        setLastSync(emailsResponse.data.lastSync);
        
        // Show notification about new emails
        if (syncResult.new_emails > 0) {
          toast.success(`${syncResult.new_emails} new email${syncResult.new_emails > 1 ? 's' : ''}`);
        }
      } else if (syncResult.full_refresh) {
        // Full refresh was done, update state
        const emailsResponse = await axios.get(`${API_URL}/api/gmail/messages`, {
          withCredentials: true,
          params: { folder: activeSection }
        });
        setEmails(emailsResponse.data.messages || []);
        setFromCache(emailsResponse.data.fromCache || false);
        setLastSync(emailsResponse.data.lastSync);
      }
      
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Error syncing emails:', error);
      }
    } finally {
      setSyncing(false);
    }
  }, [activeSection, connectionStatus.connected, syncing, toast]);

  useEffect(() => {
    if (connectionStatus.connected) {
      fetchEmails();
    }
  }, [fetchEmails, connectionStatus.connected]);

  // Connect Gmail
  const handleConnectGmail = async () => {
    try {
      setConnecting(true);
      const response = await axios.get(`${API_URL}/api/gmail/oauth/connect`, {
        withCredentials: true
      });
      
      if (response.data.auth_url) {
        // Redirect to Google OAuth
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      // Silently ignore 401 errors - session issues handled by auth context
      if (error.response?.status !== 401) {
        console.error('Error connecting Gmail:', error);
        toast.error('Failed to start Gmail connection');
      }
      setConnecting(false);
    }
  };

  // Disconnect Gmail
  const handleDisconnectGmail = async () => {
    if (!window.confirm('Are you sure you want to disconnect Gmail?')) return;
    
    try {
      await axios.post(`${API_URL}/api/gmail/oauth/disconnect`, {}, {
        withCredentials: true
      });
      toast.success('Gmail disconnected');
      setConnectionStatus({
        configured: true,
        connected: false,
        email: null,
        loading: false
      });
      setEmails([]);
    } catch (error) {
      // Silently ignore 401 errors - session issues handled by auth context
      if (error.response?.status !== 401) {
        console.error('Error disconnecting Gmail:', error);
        toast.error('Failed to disconnect Gmail');
      }
    }
  };

  const handleSelectMail = (mail) => {
    setSelectedMail(mail);
    // Mark as read
    if (!mail.isRead) {
      axios.post(`${API_URL}/api/gmail/messages/${mail.id}/read`, { read: true }, {
        withCredentials: true
      }).catch(console.error);
      
      setEmails(prev => prev.map(e => 
        e.id === mail.id ? { ...e, isRead: true } : e
      ));
    }
  };

  const handleBackToList = () => {
    setSelectedMail(null);
  };

  const handleOpenCompose = () => {
    setSendMessageOpen(true);
  };

  const handleCloseCompose = () => {
    setSendMessageOpen(false);
  };

  const handleSendEmail = async (emailData) => {
    try {
      await axios.post(`${API_URL}/api/gmail/send`, emailData, {
        withCredentials: true
      });
      toast.success('Email sent successfully!');
      handleCloseCompose();
      fetchEmails(true); // Force refresh after sending
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
      throw error;
    }
  };

  const handleToggleStar = async (emailId) => {
    const email = emails.find(e => e.id === emailId);
    const newStarred = !email?.isStarred;
    
    // Optimistic update
    setEmails(prev => prev.map(e => 
      e.id === emailId ? { ...e, isStarred: newStarred } : e
    ));
    
    try {
      await axios.post(`${API_URL}/api/gmail/messages/${emailId}/star`, 
        { starred: newStarred },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error starring message:', error);
      // Revert on error
      setEmails(prev => prev.map(e => 
        e.id === emailId ? { ...e, isStarred: !newStarred } : e
      ));
    }
  };

  const handleDeleteEmail = async (emailId) => {
    try {
      await axios.delete(`${API_URL}/api/gmail/messages/${emailId}`, {
        withCredentials: true,
        params: { folder: activeSection }
      });
      setEmails(prev => prev.filter(e => e.id !== emailId));
      if (selectedMail?.id === emailId) {
        setSelectedMail(null);
      }
      toast.success('Email moved to trash');
    } catch (error) {
      console.error('Error deleting email:', error);
      toast.error('Failed to delete email');
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleRefresh = () => {
    fetchEmails(true); // Force refresh
    toast.success('Refreshing inbox...');
  };

  // Count emails for sidebar
  const inboxCount = emails.filter(e => e.labels?.includes('INBOX')).length;
  const starredCount = emails.filter(e => e.isStarred).length;
  const sentCount = emails.filter(e => e.labels?.includes('SENT')).length;

  // Show loading state
  if (connectionStatus.loading) {
    return (
      <div className="gmail-app" data-testid="gmail-inbox">
        <div className="gmail-loading-screen">
          <Loader2 size={48} className="gmail-loading-spinner" />
          <p>Loading Gmail...</p>
        </div>
      </div>
    );
  }

  // Show connect prompt if not connected
  if (!connectionStatus.connected) {
    return (
      <div className="gmail-app" data-testid="gmail-inbox">
        <div className="gmail-connect-screen">
          <div className="gmail-connect-card">
            <div className="gmail-connect-icon">
              <img src="/gmail-logo.svg" alt="Gmail" style={{ width: '64px', height: '64px' }} />
            </div>
            <h2>Connect Gmail</h2>
            <p>
              Connect your company Gmail account to view and send emails directly from the CRM.
              All team members will have access to this shared inbox.
            </p>
            
            {connectionStatus.configured ? (
              <button 
                className="gmail-connect-btn"
                onClick={handleConnectGmail}
                disabled={connecting}
              >
                {connecting ? (
                  <>
                    <Loader2 size={20} className="gmail-loading-spinner" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <LinkIcon size={20} />
                    Connect Gmail Account
                  </>
                )}
              </button>
            ) : (
              <div className="gmail-not-configured">
                <AlertCircle size={20} />
                <span>Gmail OAuth credentials not configured. Please contact admin.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gmail-app" data-testid="gmail-inbox">
      <GmailHeader 
        onSearch={handleSearch}
        searchQuery={searchQuery}
        connectedEmail={connectionStatus.email}
        onDisconnect={handleDisconnectGmail}
      />
      <div className="gmail-body">
        <GmailSidebar
          onCompose={handleOpenCompose}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          inboxCount={inboxCount}
          starredCount={starredCount}
          sentCount={sentCount}
        />
        {selectedMail ? (
          <GmailMail
            mail={selectedMail}
            onBack={handleBackToList}
            onDelete={() => handleDeleteEmail(selectedMail.id)}
            onToggleStar={() => handleToggleStar(selectedMail.id)}
          />
        ) : (
          <GmailEmailList
            emails={emails}
            loading={loading}
            syncing={syncing}
            fromCache={fromCache}
            lastSync={lastSync}
            onSelectMail={handleSelectMail}
            onToggleStar={handleToggleStar}
            onRefresh={handleRefresh}
            activeSection={activeSection}
          />
        )}
      </div>
      {sendMessageOpen && (
        <GmailSendMail
          onClose={handleCloseCompose}
          onSend={handleSendEmail}
          fromEmail={connectionStatus.email}
        />
      )}
    </div>
  );
};

export default GmailInbox;
